import { useMutation, useQuery } from '@apollo/client';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import Button from '../../../core/components/Button';
import Checkbox from '../../../core/components/Checkbox';
import CheckboxGroup from '../../../core/components/CheckboxGroup';
import StringField from '../../../core/components/Fields/StringField';
import FormField from '../../../core/components/FormField';
import Layout from '../../../core/components/Layout';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries';
import Page from '../../../core/components/Page';
import SaveStatus from '../../../core/components/SaveStatus';
import { SecretField } from '../../../core/components/Secret';
import SettingsFormCard from '../../../core/components/SettingsFormCard';
import SettingsNavigation from '../../../core/components/SettingsNavigation';
import { useToaster } from '../../../core/components/Toast';
import countFieldErrors from '../../../core/utils/countFieldErrors';
import { url } from '../../../core/utils/link';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';
import { CUSTOMER_INTEGRATION_CUSTOM_PATH } from '../../constants';
import { GET_CUSTOM_INTEGRATIONS, UPDATE_CUSTOM_INTEGRATION } from '../../queries';

const FORM_DEFAULT_VALUES = {
    name: '',
    enabled: true,
    baseUrl: '',
    authId: '',
    authToken: '',
    config: {} as Record<string, any>,
    extraData: {} as Record<string, any>,
    orderChannelIds: [],
    rfqChannelIds: [],
};
const CustomIntegrationEditPage = () => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();
    const navigate = useNavigate();
    const { integrationId } = useParams();
    const backTooltipText = `${t('backTooltipText')} ${t('customIntegration.title')}`;

    const { data, refetch } = useQuery(GET_CUSTOM_INTEGRATIONS, {
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
    });
    const integration = data?.customIntegrations?.find((i) => i.id === integrationId);

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });

    const [updateIntegration, { loading: isSubmitting }] = useMutation(UPDATE_CUSTOM_INTEGRATION);
    const handleUpdateIntegration = async (formData) => {
        try {
            await updateIntegration({
                variables: {
                    id: integration.id,
                    name: formData.name,
                    enabled: formData.enabled,
                    baseUrl: formData.baseUrl,
                    authId: formData.authId || null,
                    authToken: formData.authToken || null,
                    config: formData.config && Object.keys(formData.config).length > 0 ? formData.config : null,
                    extraData:
                        formData.extraData && Object.keys(formData.extraData).length > 0 ? formData.extraData : null,
                    orderChannels: formData.orderChannelIds,
                    rfqChannels: formData.rfqChannelIds,
                },
            });
            await refetch();
            publishToast({ description: t('customIntegration.updateSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_CUSTOM_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('customIntegration.updateError'), status: 'error' });
        }
    };

    const formErrors = form.formState.errors;

    useEffect(() => {
        if (integration) {
            // Convert authToken from JSON object to string for display
            const authTokenString =
                integration.authToken && typeof integration.authToken === 'object'
                    ? JSON.stringify(integration.authToken)
                    : integration.authToken || '';

            // Parse config if it's a string
            let configValue = {};
            try {
                configValue = integration.config
                    ? typeof integration.config === 'string'
                        ? JSON.parse(integration.config)
                        : integration.config
                    : {};
            } catch (e) {
                configValue = {};
            }

            // Parse extraData if it's a string
            let extraDataValue = {};
            try {
                extraDataValue = integration.extraData
                    ? typeof integration.extraData === 'string'
                        ? JSON.parse(integration.extraData)
                        : integration.extraData
                    : {};
            } catch (e) {
                extraDataValue = {};
            }

            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                enabled: integration.enabled !== undefined ? integration.enabled : true,
                baseUrl: integration.baseUrl,
                authId: integration.authId || '',
                authToken: authTokenString,
                config: configValue,
                extraData: extraDataValue,
                orderChannelIds: integration.orderChannels.map((channel) => channel.id),
                rfqChannelIds: integration.rfqChannels.map((channel) => channel.id),
            });
        }
    }, [integration]);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];
    const rfqChannels = channelData?.rfqProcessingChannels || [];

    // Parse config for display (it comes as a string from GraphQL JSONString scalar)
    let parsedConfig;
    try {
        if (integration?.config) {
            parsedConfig = typeof integration.config === 'string' ? JSON.parse(integration.config) : integration.config;
        } else {
            parsedConfig = null;
        }
    } catch (e) {
        parsedConfig = null;
    }

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header>
                    <IntegrationBreadcrumb
                        backTooltip={backTooltipText}
                        items={[
                            { label: t('customIntegration.title'), to: url(CUSTOMER_INTEGRATION_CUSTOM_PATH) },
                            { label: integration?.name || t('customIntegration.form.updateTitle') },
                        ]}
                    />
                </Page.Header>
                <Page.Content lowered className="flex flex-col gap-6">
                    <SettingsFormCard
                        onSubmit={form.handleSubmit(handleUpdateIntegration)}
                        title={t('customIntegration.form.updateTitle')}
                    >
                        <Controller
                            name="name"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('customIntegration.form.name')} error={formErrors.name}>
                                    <StringField {...field} />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="enabled"
                            control={form.control}
                            render={({ field }) => (
                                <label className="flex gap-3 items-start cursor-pointer">
                                    <Checkbox
                                        checked={field.value}
                                        onChange={(e) => field.onChange(e.target.checked)}
                                    />
                                    <div className="flex flex-col gap-1">
                                        <span className="text-content-strong text-sm">
                                            {t('customIntegration.form.enabled')}
                                        </span>
                                        <span className="text-content-medium text-xs">
                                            {t('customIntegration.form.enabledDescription')}
                                        </span>
                                    </div>
                                </label>
                            )}
                        />

                        <Controller
                            name="baseUrl"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('customIntegration.form.baseUrl')} error={formErrors.baseUrl}>
                                    <StringField
                                        {...field}
                                        placeholder={t('customIntegration.form.baseUrlPlaceholder')}
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="authId"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('customIntegration.form.authId')} error={formErrors.authId}>
                                    <StringField
                                        {...field}
                                        placeholder={t('customIntegration.form.authIdPlaceholder')}
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="authToken"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('customIntegration.form.authToken')} error={formErrors.authToken}>
                                    <SecretField
                                        {...field}
                                        secretName="authToken"
                                        secretId={integration?.id}
                                        defaultIsVisible={false}
                                    />
                                </FormField>
                            )}
                        />

                        {integration && (
                            <FormField label={t('customIntegration.form.authState')}>
                                <StringField value={integration.authState || ''} readOnly={true} />
                            </FormField>
                        )}

                        {integration && parsedConfig && (
                            <FormField label={t('customIntegration.form.config')}>
                                <pre className="inline-block text-sm rounded bg-surface-strong p-2.5 text-content-strong">
                                    {JSON.stringify(parsedConfig, null, 4)}
                                </pre>
                            </FormField>
                        )}

                        <Controller
                            name="orderChannelIds"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('customIntegration.form.orderChannels')}
                                    error={formErrors.orderChannelIds}
                                >
                                    <CheckboxGroup
                                        {...field}
                                        options={
                                            orderChannels.map((channel) => ({
                                                value: channel.id,
                                                label: channel.name,
                                            })) || []
                                        }
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="rfqChannelIds"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('customIntegration.form.rfqChannels')}
                                    error={formErrors.rfqChannelIds}
                                >
                                    <CheckboxGroup
                                        {...field}
                                        options={
                                            rfqChannels.map((channel) => ({
                                                value: channel.id,
                                                label: channel.name,
                                            })) || []
                                        }
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />
                        <SaveStatus
                            isDirty={form.formState.isDirty}
                            isSubmitting={isSubmitting}
                            errorCount={countFieldErrors(form.formState.errors)}
                        >
                            <Button type="button" asChild>
                                <Link to={url(CUSTOMER_INTEGRATION_CUSTOM_PATH)}>
                                    {t('customIntegration.form.cancelButton')}
                                </Link>
                            </Button>
                        </SaveStatus>
                    </SettingsFormCard>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default CustomIntegrationEditPage;
